import { type RouteConfig, index, route } from '@react-router/dev/routes';

const quizEnabled = import.meta.env.VITE_QUIZ_ENABLED === 'true';
const marchActivitiesLocked = import.meta.env.VITE_MARCH_ACTIVITIES_LOCKED === 'true';
const adminUsersEnabled = import.meta.env.VITE_ADMIN_USERS_ENABLED === 'true';
const adminCoursesEnabled = import.meta.env.VITE_ADMIN_COURSES_ENABLED === 'true';
const adminDashboardEnabled = import.meta.env.VITE_ADMIN_DASHBOARD_ENABLED === 'true';
const adminTaxonomyEnabled = import.meta.env.VITE_ADMIN_TAXONOMY_ENABLED === 'true';

interface RouteDefinition {
  path: string;
  file: string;
  isPublic?: boolean;
}

const routeDefinitions: RouteDefinition[] = [
  { path: '/', file: 'routes/index.tsx', isPublic: true },
  { path: '/cursos', file: 'routes/home.tsx' },
  { path: '/profile', file: 'routes/profile.tsx' },
  ...(adminDashboardEnabled
    ? [{ path: '/admin/dashboard', file: 'routes/admin/dashboard.tsx' }]
    : []),
  ...(adminUsersEnabled
    ? [
        { path: '/admin/usuarios', file: 'routes/users/index.tsx' },
        { path: '/admin/usuarios/:userId', file: 'routes/users.$userId/index.tsx' },
      ]
    : []),
  { path: '/gerenciar-usuarios', file: 'routes/users.tsx' },
  ...(adminCoursesEnabled
    ? [
        { path: '/admin/cursos/professores', file: 'routes/teachers.tsx' },
        { path: '/admin/cursos', file: 'routes/courses.tsx' },
        { path: '/admin/cursos/novo', file: 'routes/course-new.tsx' },
        { path: '/admin/cursos/editar/:id', file: 'routes/course-edit.tsx' },
        { path: '/admin/cursos/:id/modulos', file: 'routes/course-modules.tsx' },
      ]
    : []),
  ...(adminTaxonomyEnabled
    ? [{ path: '/admin/taxonomia', file: 'routes/taxonomy/index.tsx' }]
    : []),
  { path: '/course', file: 'routes/course-detail.tsx', isPublic: true },
  { path: '/course/lesson', file: 'routes/lesson-detail.tsx' },
  ...(marchActivitiesLocked
    ? []
    : [
        { path: '/dashboard', file: 'routes/dashboardAnalytics/index.tsx' },
        { path: '/dashboard/subtopics', file: 'routes/dashboardAnalytics/subtopics.tsx' },
        { path: '/cronograma', file: 'routes/schedule/index.tsx' },
        { path: '/cronograma/configurar', file: 'routes/schedule-onboarding/index.tsx' },
        { path: '/admin/cupons', file: 'routes/cupons/index.tsx' },
      ]),
  ...(quizEnabled
    ? [
        { path: '/atividades', file: 'routes/quiz/index.tsx' },
        { path: '/atividades/criar/:type', file: 'routes/quiz-create/index.tsx' },
        { path: '/atividades/confirmacao', file: 'routes/quiz.confirmacao.tsx' },
        { path: '/atividades/caderno-de-erros', file: 'routes/error-notebook/index.tsx' },
        { path: '/atividades/:id', file: 'routes/quiz.$id/index.tsx' },
        { path: '/admin/questoes', file: 'routes/questions/index.tsx' },
        { path: '/admin/questoes/importar', file: 'routes/questions/importar/index.tsx' },
        { path: '/admin/questoes/reports', file: 'routes/questions/reports/index.tsx' },
      ]
    : []),
];

const routes: RouteConfig = routeDefinitions.map((def) => {
  if (def.path === '/') {
    return index(def.file);
  }
  return route(def.path, def.file);
});

const PUBLIC_ROUTES = routeDefinitions.filter((def) => def.isPublic).map((def) => def.path);

export function isPublicRoute(pathname: string): boolean {
  const pathWithoutQuery = pathname.split('?')[0].split('#')[0];

  return PUBLIC_ROUTES.some((route) => {
    const pattern = route.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${pattern}$`);
    return regex.test(pathWithoutQuery);
  });
}

export function isProtectedRoute(pathname: string): boolean {
  return !isPublicRoute(pathname);
}

export default routes satisfies RouteConfig;
